import { useState } from 'react';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { submitEnquiry } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const EnquiryForm = ({ tradieId, tradieName }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitEnquiry({
        tradie_id: tradieId,
        ...formData
      });
      setSubmitted(true);
      trackEvent('enquiry_submit', {
        tradie_id: tradieId,
        tradie_name: tradieName,
      });
      toast.success('Enquiry sent successfully!', {
        description: `${tradieName} will be in touch soon.`
      });
    } catch (error) {
      toast.error('Failed to send enquiry', {
        description: error.response?.data?.detail || 'Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
          <Send className="w-8 h-8 text-secondary" />
        </div>
        <h3 className="font-heading font-semibold text-lg mb-2">Enquiry Sent!</h3>
        <p className="text-muted-foreground text-sm">
          {tradieName} will contact you soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="enquiry-form">
      <div className="space-y-2">
        <Label htmlFor="name">Your Name *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="John Smith"
          required
          data-testid="enquiry-name"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email Address *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john@example.com"
          required
          data-testid="enquiry-email"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number</Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="021 123 4567"
          data-testid="enquiry-phone"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Describe what you need help with..."
          rows={4}
          required
          data-testid="enquiry-message"
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-primary hover:bg-primary/90"
        disabled={loading}
        data-testid="enquiry-submit"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Enquiry
          </>
        )}
      </Button>
    </form>
  );
};

export default EnquiryForm;
